import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { handleNumericChange } from "@/core/helpers/handleNumericChange";
import useAppSelector from "@/core/helpers/useAppSelector";
import { Button, Modal } from "@/shared/ui";
import Loader from "@/shared/ui/Loader";
import OfferCheckbox from "@/shared/widgets/OfferCheckbox";
import OfferPlayerInfo from "@/shared/widgets/OfferPlayerInfo";
import OfferView from "@/shared/widgets/OfferView";
import { offerCreatorSelector } from "@/domains/offer/slice";
import { useOfferCreatorVehicle } from "@/domains/offer/hooks/useOfferCreatorVehicle";
import OfferVehicleInfo from "@/domains/offer/components/vehicle/OfferVehicleInfo";
import OfferVehicleUpgrade from "@/domains/offer/components/vehicle/OfferVehicleUpgrade";
import OfferVehicleViewContent from "@/domains/offer/components/vehicle/OfferVehicleViewContent";
import VehicleOfferPriceOffer from "@/domains/offer/components/vehicle/VehicleOfferPriceOffer";
import VehicleOfferUpgradeButton from "@/domains/offer/components/vehicle/VehicleOfferUpgradeButton";

export default function OfferEstateVehicleCreatorContainer({ isShow }: { isShow: boolean }) {
  const { t } = useTranslation("offer");

  const offerCreator = useAppSelector(offerCreatorSelector);

  const [isAgree, setIsAgree] = useState(false);
  const [price, setPrice] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [isUpgradeOpen, setIsUpgradeOpen] = useState(false);

  const { vehicles, create, close, pending, loading } = useOfferCreatorVehicle(isShow);

  const selectedVehicle = vehicles[activeIndex];

  const upgradeCount = selectedVehicle?.upgrade
    ? Object.values(selectedVehicle?.upgrade).filter((item) => !!item).length
    : 0;

  const onSubmit = useCallback(() => {
    if (!selectedVehicle) {
      return;
    }
    create(offerCreator?.targetCharacter?.id, selectedVehicle?.vehicleId, parseInt(price, 10));
  }, [selectedVehicle, create, offerCreator?.targetCharacter?.id, price]);

  const toggleAgree = useCallback(() => {
    setIsAgree((prev) => !prev);
  }, []);

  const openUpgrade = useCallback(() => setIsUpgradeOpen(true), []);
  const closeUpgrade = useCallback(() => setIsUpgradeOpen(false), []);
  const handleInputChange = (value: string) => handleNumericChange(value, setPrice);

  const isDisabled = useMemo(
    () => pending || !isAgree || !price.length,
    [pending, isAgree, price.length],
  );

  const notEmpty = !pending && !loading && vehicles?.length > 0;

  const footer = (
    <Button
      className={clsx("h-11", !notEmpty && "!opacity-0 pointer-events-none")}
      onClick={onSubmit}
      disabled={isDisabled}
      loading={pending}
    >
      {t("offer.accept")}
    </Button>
  );

  useEffect(() => {
    if (!isShow) {
      setIsAgree(false);
      setPrice("");
      setActiveIndex(0);
      setIsUpgradeOpen(false);
    }
  }, [isShow]);

  return (
    <Modal
      open={isShow}
      title={t("offer.vehicle.creator.title")}
      onClose={close}
      footer={footer}
      style={{ fontFamily: "TT Norms Pro" }}
    >
      <div className="relative flex flex-col min-h-[38.25rem]">
        {!notEmpty || pending || loading ? (
          <div className="absolute top-0 left-0 z-3 flex justify-center items-center size-full text-16 font-bold text-white/50 text-center leading-none uppercase">
            {!pending && !loading && t("offer.empty")}
            {(pending || loading) && <Loader />}
          </div>
        ) : (
          <>
            <OfferPlayerInfo
              text={t("offer.buyer")}
              id={offerCreator?.targetCharacter?.id}
              name={offerCreator?.targetCharacter?.name}
              avatar={offerCreator?.targetCharacter?.avatar}
            />

            <p className="mt-4 mb-3.5 text-16 font-medium">{t("offer.vehicle.creator.vehicle")}:</p>

            <OfferView
              list={vehicles || []}
              activeIndex={activeIndex}
              setActiveIndex={setActiveIndex}
            >
              <OfferVehicleViewContent selectedVehicle={selectedVehicle} />
            </OfferView>

            <OfferVehicleInfo
              vehicle={{
                plateNumber: selectedVehicle?.plateNumber,
                mileage: selectedVehicle?.mileage,
                condition: selectedVehicle?.condition,
              }}
            />

            <VehicleOfferUpgradeButton count={upgradeCount} onClick={openUpgrade} />

            <VehicleOfferPriceOffer price={price} setPrice={handleInputChange} />

            <OfferCheckbox
              isChecked={isAgree}
              onChange={toggleAgree}
              label={t("offer.vehicle.creator.agree")}
            />

            {isUpgradeOpen && selectedVehicle?.upgrade && (
              <OfferVehicleUpgrade
                upgrade={selectedVehicle?.upgrade}
                vin={selectedVehicle?.vinNumber}
                maxInventoryWeight={selectedVehicle?.maxInventoryWeight}
                onClose={closeUpgrade}
              />
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
